import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAIExtracts, SharedExtract } from '../hooks/useAIExtracts'
import { KeyPoint, Flashcard } from '../lib/supabase'

export function SharedExtractPage() {
  const { token } = useParams<{ token: string }>()
  const { getSharedExtract, exportAsMarkdown, copyToClipboard } = useAIExtracts(undefined)
  const [extract, setExtract] = useState<SharedExtract | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [activeFlashcard, setActiveFlashcard] = useState<number>(0)
  const [showAnswer, setShowAnswer] = useState(false)

  useEffect(() => {
    async function loadExtract() {
      if (!token) {
        setError('Invalid share link')
        setLoading(false)
        return
      }

      const data = await getSharedExtract(token)
      if (data) {
        setExtract(data)
      } else {
        setError('This shared content could not be found or has expired.')
      }
      setLoading(false)
    }

    loadExtract()
  }, [token, getSharedExtract])

  const handleCopyMarkdown = async () => {
    if (extract) {
      const markdown = exportAsMarkdown(extract)
      const success = await copyToClipboard(markdown)
      if (success) {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    }
  }

  const handleNextCard = () => {
    if (extract?.type === 'flashcards' && Array.isArray(extract.content.extractContent)) {
      const cards = extract.content.extractContent as Flashcard[]
      setActiveFlashcard(prev => (prev + 1) % cards.length)
      setShowAnswer(false)
    }
  }

  const handlePrevCard = () => {
    if (extract?.type === 'flashcards' && Array.isArray(extract.content.extractContent)) {
      const cards = extract.content.extractContent as Flashcard[]
      setActiveFlashcard(prev => (prev - 1 + cards.length) % cards.length)
      setShowAnswer(false)
    }
  }

  if (loading) {
    return (
      <div className="extract-page loading">
        <div className="spinner" />
        <p>Loading shared content...</p>
        <style>{pageStyles}</style>
      </div>
    )
  }

  if (error || !extract) {
    return (
      <div className="extract-page error">
        <div className="error-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
        </div>
        <h1>Content Not Found</h1>
        <p>{error || 'This shared content could not be found.'}</p>
        <Link to="/" className="btn-primary">Go to Read Later</Link>
        <style>{pageStyles}</style>
      </div>
    )
  }

  const typeLabel = {
    summary: 'AI Summary',
    key_points: 'Key Points',
    flashcards: 'Flashcards',
    synthesis: 'AI Synthesis'
  }[extract.type]

  return (
    <div className="extract-page">
      {/* Header */}
      <header className="extract-header">
        <Link to="/" className="extract-logo">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
          <span>Read Later</span>
        </Link>
        <div className="header-actions">
          <button className="copy-btn" onClick={handleCopyMarkdown}>
            {copied ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
                Copy
              </>
            )}
          </button>
        </div>
      </header>

      {/* Extract type badge */}
      <div className="type-badge-container">
        <span className={`type-badge ${extract.type}`}>
          {getTypeIcon(extract.type)}
          {typeLabel}
        </span>
      </div>

      {/* Main content */}
      <main className="extract-content">
        <h1>{extract.content.title}</h1>

        {/* Source info */}
        <div className="source-info">
          <span>From:</span>
          <a
            href={extract.content.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="source-link"
          >
            {extract.content.sourceTitle}
          </a>
        </div>

        {/* Render based on type */}
        {extract.type === 'summary' && (
          <div className="summary-content">
            {typeof extract.content.extractContent === 'string' && (
              <p>{extract.content.extractContent}</p>
            )}
          </div>
        )}

        {extract.type === 'key_points' && Array.isArray(extract.content.extractContent) && (
          <div className="key-points-content">
            <ol>
              {(extract.content.extractContent as KeyPoint[]).map((kp) => (
                <li key={kp.id} className={`importance-${kp.importance}`}>
                  <span className="kp-text">{kp.text}</span>
                  <span className={`importance-badge ${kp.importance}`}>
                    {kp.importance}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {extract.type === 'flashcards' && Array.isArray(extract.content.extractContent) && (
          <div className="flashcards-content">
            {(() => {
              const cards = extract.content.extractContent as Flashcard[]
              const card = cards[activeFlashcard]
              return (
                <>
                  <div className="flashcard-counter">
                    Card {activeFlashcard + 1} of {cards.length}
                  </div>
                  <div
                    className={`flashcard-display ${showAnswer ? 'flipped' : ''}`}
                    onClick={() => setShowAnswer(!showAnswer)}
                  >
                    <div className="card-face front">
                      <span className="card-label">Question</span>
                      <p>{card.front}</p>
                      <span className="tap-hint">Tap to reveal answer</span>
                    </div>
                    <div className="card-face back">
                      <span className="card-label">Answer</span>
                      <p>{card.back}</p>
                      {card.tags && card.tags.length > 0 && (
                        <div className="card-tags">
                          {card.tags.map(tag => (
                            <span key={tag} className="tag">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flashcard-nav">
                    <button onClick={handlePrevCard} disabled={cards.length <= 1}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                      Previous
                    </button>
                    <button onClick={handleNextCard} disabled={cards.length <= 1}>
                      Next
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </button>
                  </div>
                </>
              )
            })()}
          </div>
        )}
      </main>

      {/* CTA Footer */}
      <footer className="extract-cta">
        <div className="cta-content">
          <div className="ai-badge">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            Generated by Read Later AI
          </div>
          <h2>Save articles, generate insights</h2>
          <p>Turn any article into summaries, key points, and flashcards with AI.</p>
          <Link to="/app" className="btn-primary btn-large">
            Try Read Later Free
          </Link>
        </div>
      </footer>

      <style>{pageStyles}</style>
    </div>
  )
}

function getTypeIcon(type: string) {
  switch (type) {
    case 'summary':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
        </svg>
      )
    case 'key_points':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
        </svg>
      )
    case 'flashcards':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <path d="M12 12h.01" />
        </svg>
      )
    default:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
      )
  }
}

const pageStyles = `
  .extract-page {
    min-height: 100vh;
    background: var(--landing-bg, #fafaf9);
    color: var(--landing-text, #1c1917);
  }

  @media (prefers-color-scheme: dark) {
    .extract-page {
      background: #0a0a0a;
      color: #fafaf9;
    }
  }

  .extract-page.loading,
  .extract-page.error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem;
    text-align: center;
  }

  .error-icon {
    color: var(--accent, #f97316);
    opacity: 0.5;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(249, 115, 22, 0.2);
    border-top-color: #f97316;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .extract-header {
    position: sticky;
    top: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    background: rgba(250, 250, 249, 0.9);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--landing-border, #e7e5e4);
  }

  @media (prefers-color-scheme: dark) {
    .extract-header {
      background: rgba(10, 10, 10, 0.9);
      border-color: #27272a;
    }
  }

  .extract-logo {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 700;
    font-size: 1.125rem;
    color: inherit;
    text-decoration: none;
  }

  .extract-logo svg {
    color: var(--accent, #f97316);
  }

  .header-actions {
    display: flex;
    gap: 0.5rem;
  }

  .copy-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: var(--bg-secondary, #f5f5f4);
    border: 1px solid var(--landing-border, #e7e5e4);
    border-radius: 8px;
    color: inherit;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  @media (prefers-color-scheme: dark) {
    .copy-btn {
      background: #1a1a1a;
      border-color: #333;
    }
  }

  .copy-btn:hover {
    border-color: var(--accent, #f97316);
  }

  .type-badge-container {
    display: flex;
    justify-content: center;
    padding: 1.5rem 1rem 0;
  }

  .type-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: rgba(249, 115, 22, 0.1);
    color: var(--accent, #f97316);
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .type-badge.summary {
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  }

  .type-badge.key_points {
    background: rgba(34, 197, 94, 0.1);
    color: #22c55e;
  }

  .type-badge.flashcards {
    background: rgba(168, 85, 247, 0.1);
    color: #a855f7;
  }

  .extract-content {
    max-width: 680px;
    margin: 0 auto;
    padding: 2rem 1.5rem;
  }

  .extract-content h1 {
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1.3;
    margin-bottom: 1rem;
  }

  .source-info {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--text-secondary, #78716c);
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--landing-border, #e7e5e4);
  }

  @media (prefers-color-scheme: dark) {
    .source-info {
      border-color: #333;
    }
  }

  .source-link {
    color: var(--accent, #f97316);
    text-decoration: none;
  }

  .source-link:hover {
    text-decoration: underline;
  }

  /* Summary styles */
  .summary-content {
    font-family: Georgia, serif;
    font-size: 1.125rem;
    line-height: 1.8;
  }

  .summary-content p {
    margin-bottom: 1.5em;
  }

  /* Key points styles */
  .key-points-content ol {
    list-style: none;
    padding: 0;
    margin: 0;
    counter-reset: kp-counter;
  }

  .key-points-content li {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
    background: var(--bg-secondary, #f5f5f4);
    border-radius: 12px;
    margin-bottom: 0.75rem;
    counter-increment: kp-counter;
    position: relative;
  }

  @media (prefers-color-scheme: dark) {
    .key-points-content li {
      background: #1a1a1a;
    }
  }

  .key-points-content li::before {
    content: counter(kp-counter);
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    background: var(--accent, #f97316);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .kp-text {
    flex: 1;
    font-size: 1rem;
    line-height: 1.6;
  }

  .importance-badge {
    flex-shrink: 0;
    font-size: 0.625rem;
    text-transform: uppercase;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-weight: 600;
  }

  .importance-badge.high {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
  }

  .importance-badge.medium {
    background: rgba(249, 115, 22, 0.2);
    color: #f97316;
  }

  .importance-badge.low {
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
  }

  /* Flashcards styles */
  .flashcards-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .flashcard-counter {
    font-size: 0.875rem;
    color: var(--text-secondary, #78716c);
    margin-bottom: 1rem;
  }

  .flashcard-display {
    width: 100%;
    max-width: 400px;
    min-height: 280px;
    perspective: 1000px;
    cursor: pointer;
    margin-bottom: 1.5rem;
  }

  .card-face {
    position: relative;
    width: 100%;
    min-height: 280px;
    padding: 2rem;
    background: var(--bg-secondary, #f5f5f4);
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    transition: transform 0.6s, opacity 0.3s;
    backface-visibility: hidden;
  }

  @media (prefers-color-scheme: dark) {
    .card-face {
      background: #1a1a1a;
    }
  }

  .card-face.front {
    border: 2px solid var(--accent, #f97316);
  }

  .card-face.back {
    position: absolute;
    top: 0;
    left: 0;
    border: 2px solid #22c55e;
    transform: rotateY(180deg);
  }

  .flashcard-display.flipped .card-face.front {
    transform: rotateY(180deg);
    opacity: 0;
  }

  .flashcard-display.flipped .card-face.back {
    transform: rotateY(360deg);
    opacity: 1;
  }

  .card-label {
    position: absolute;
    top: 1rem;
    left: 1rem;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .card-face.front .card-label {
    color: var(--accent, #f97316);
  }

  .card-face.back .card-label {
    color: #22c55e;
  }

  .card-face p {
    font-size: 1.125rem;
    line-height: 1.6;
    margin: 0;
  }

  .tap-hint {
    position: absolute;
    bottom: 1rem;
    font-size: 0.75rem;
    color: var(--text-secondary, #78716c);
  }

  .card-tags {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-top: 1rem;
  }

  .card-tags .tag {
    font-size: 0.625rem;
    padding: 0.25rem 0.5rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    color: var(--text-secondary, #78716c);
  }

  .flashcard-nav {
    display: flex;
    gap: 1rem;
  }

  .flashcard-nav button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: var(--bg-secondary, #f5f5f4);
    border: 1px solid var(--landing-border, #e7e5e4);
    border-radius: 8px;
    color: inherit;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  @media (prefers-color-scheme: dark) {
    .flashcard-nav button {
      background: #1a1a1a;
      border-color: #333;
    }
  }

  .flashcard-nav button:hover:not(:disabled) {
    border-color: var(--accent, #f97316);
  }

  .flashcard-nav button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* CTA Footer */
  .extract-cta {
    background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
    padding: 4rem 1.5rem;
    margin-top: 3rem;
  }

  @media (prefers-color-scheme: light) {
    .extract-cta {
      background: linear-gradient(135deg, #f97316 0%, #fb923c 100%);
    }
  }

  .cta-content {
    max-width: 500px;
    margin: 0 auto;
    text-align: center;
    color: white;
  }

  .ai-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    font-size: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .cta-content h2 {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .cta-content p {
    opacity: 0.9;
    margin-bottom: 1.5rem;
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: #f97316;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    text-decoration: none;
    border: none;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-primary:hover {
    background: #ea580c;
  }

  @media (prefers-color-scheme: light) {
    .cta-content .btn-primary {
      background: white;
      color: #f97316;
    }

    .cta-content .btn-primary:hover {
      background: rgba(255, 255, 255, 0.9);
    }
  }

  .btn-large {
    padding: 1rem 2rem;
    font-size: 1.125rem;
  }

  @media (max-width: 600px) {
    .extract-content h1 {
      font-size: 1.5rem;
    }

    .summary-content {
      font-size: 1rem;
    }

    .cta-content h2 {
      font-size: 1.5rem;
    }

    .flashcard-display {
      min-height: 240px;
    }

    .card-face {
      min-height: 240px;
      padding: 1.5rem;
    }
  }
`
